import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import ProductCard from '../components/shared/ProductCard';
import { useSocket } from '../context/SocketContext';
import { useAuth } from '../context/AuthContext';

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [loading, setLoading] = useState(true);
  const { socket } = useSocket();
  const { user } = useAuth();
  const [entered, setEntered] = useState(false);

  // ✅ Zoom-in entry animation trigger
  useEffect(() => {
    const t = setTimeout(() => setEntered(true), 50);
    return () => clearTimeout(t);
  }, []);

  const fetchFeatured = async () => {
    try {
      const res = await api.get('/products?featured=true&limit=4');
      if (res.data.products.length === 0) {
        const fallback = await api.get('/products?limit=4');
        setFeatured(fallback.data.products);
      } else {
        setFeatured(res.data.products);
      }
    } catch { }
    setLoading(false);
  };

  useEffect(() => {
    fetchFeatured();
  }, []);

  useEffect(() => {
    if (!socket) return;
    socket.on('product:updated', () => fetchFeatured());
    socket.on('product:created', () => fetchFeatured());
    socket.on('product:deleted', () => fetchFeatured());
    return () => {
      socket.off('product:updated');
      socket.off('product:created');
      socket.off('product:deleted');
    };
  }, [socket]);

  const categories = [
    { name: 'Women', image: 'https://images.unsplash.com/photo-1490481651871-ab68de25d43d?w=800&q=80', href: '/shop?category=women' },
    { name: 'Men', image: 'https://images.unsplash.com/photo-1488161628813-04466f872be2?w=800&q=80', href: '/shop?category=men' },
  ];

  return (
    <>
      <style>{`
        @keyframes zoomInReveal {
          0%   { opacity: 0; transform: scale(0.82); filter: blur(6px); }
          60%  { opacity: 1; transform: scale(1.02); filter: blur(0px); }
          100% { opacity: 1; transform: scale(1);    filter: blur(0px); }
        }

        .page-zoom-enter {
          animation: zoomInReveal 0.85s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }

        .page-hidden {
          opacity: 0;
        }
      `}</style>

      {/* ✅ Entire page wrapped — zoom-in fires on mount */}
      <div className={`flex flex-col ${entered ? 'page-zoom-enter' : 'page-hidden'}`}>
        
        {/* ── HERO ─────────────────────────────────────────────── */}
        <section className="relative h-[calc(100vh-84px)] md:h-[calc(100vh-104px)] w-full flex items-center bg-[#E5E5E5] overflow-hidden">
          <div className="absolute inset-0 w-full h-full">
            <video autoPlay muted loop playsInline className="w-full h-full object-cover">
              <source src="/75476-556034327.mp4" type="video/mp4" />
            </video>
            <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/40 to-transparent w-1/2" />
          </div>

          <div className="relative z-10 w-full px-10 md:px-24">
            <div className="max-w-xl">
              <p className="text-[10px] uppercase tracking-[0.5em] text-gray-500 mb-8">
                Monsoon / Festive 2024
              </p>
              <h1 className="font-display text-7xl md:text-[120px] leading-[0.85] text-lux-primary font-light mb-12 tracking-tighter">
                Varanasi <br />
                <span className="italic font-normal">Silk.</span>
              </h1>

              <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12 mt-12">
                <Link
                  to="/design-editor"
                  className="group relative bg-lux-gold text-white px-10 py-4 text-[11px] uppercase tracking-[0.4em] font-semibold overflow-hidden transition-all duration-500 hover:shadow-2xl hover:shadow-lux-gold/20"
                >
                  <span className="relative z-10">Design on Your Own</span>
                  <div className="absolute inset-0 bg-lux-primary translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
                </Link>

                <div className="flex items-center gap-8">
                  <Link
                    to="/shop"
                    className="group relative text-[11px] uppercase tracking-[0.3em] font-medium overflow-hidden"
                  >
                    <span className="inline-block transition-transform duration-300 group-hover:-translate-y-full">
                      Shop Collection
                    </span>
                    <span className="absolute left-0 top-full inline-block transition-transform duration-300 group-hover:-translate-y-full text-lux-gold">
                      Shop Collection
                    </span>
                    <div className="h-px w-full bg-black mt-1" />
                  </Link>

                  {user && (
                    <Link
                      to={user.role === 'seller' ? '/seller/dashboard' : '/orders'}
                      className="group relative text-[11px] uppercase tracking-[0.3em] font-medium overflow-hidden text-gray-400 hover:text-black transition-colors"
                    >
                      <span className="inline-block transition-transform duration-300 group-hover:-translate-y-full">
                        {user.role === 'seller' ? 'Go to Portal' : 'View History'}
                      </span>
                      <span className="absolute left-0 top-full inline-block transition-transform duration-300 group-hover:-translate-y-full text-lux-gold">
                        {user.role === 'seller' ? 'Go to Portal' : 'View History'}
                      </span>
                      <div className="h-px w-full bg-black/10 mt-1" />
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CATEGORIES ───────────────────────────────────────── */}
        <section className="py-24 md:py-32 px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 max-w-5xl mx-auto">
            {categories.map((cat) => (
              <Link key={cat.name} to={cat.href} className="group block text-center">
                <div className="overflow-hidden aspect-[3/4] mb-6 bg-gray-100">
                  <img
                    src={cat.image}
                    alt={cat.name}
                    className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-[1.02] transition-all duration-1000 ease-out"
                  />
                </div>
                <h3 className="font-display text-2xl font-light tracking-tight">{cat.name}</h3>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gray-400 mt-2">Discover</p>
              </Link>
            ))}
          </div>
        </section>

        {/* ── FEATURED PRODUCTS ────────────────────────────────── */}
        <section className="py-24 bg-[#FAFAFA]">
          <div className="max-w-7xl mx-auto px-6 md:px-12 mb-16 flex justify-between items-end">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-light tracking-tighter">
                The Essentials
              </h2>
              <p className="text-sm text-gray-500 font-light mt-2 uppercase tracking-widest">
                Selected pieces for a timeless wardrobe
              </p>
            </div>
            <Link
              to="/shop"
              className="text-xs uppercase tracking-widest text-gray-400 hover:text-black transition-colors"
            >
              Browse All
            </Link>
          </div>

          <div className="max-w-7xl mx-auto px-6 md:px-12">
            {loading ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="animate-pulse">
                    <div className="bg-gray-200 aspect-[3/4] mb-4" />
                    <div className="h-4 bg-gray-200 w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 w-1/4" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
                {featured.map((product) => (
                  <ProductCard key={product._id} product={product} />
                ))}
              </div>
            )}
          </div>
        </section>

        {/* ── BRAND STATEMENT ──────────────────────────────────── */}
        <section className="py-40 px-6 bg-white flex justify-center">
          <div className="max-w-2xl text-center">
            <span className="text-[10px] uppercase tracking-[0.5em] text-lux-gold mb-8 block">
              Our Ethos
            </span>
            <h2 className="font-display text-3xl md:text-5xl font-light leading-tight mb-10 text-lux-primary">
              "We believe in the beauty of simplicity and the permanence of{' '}
              <span className="italic">quality.</span>"
            </h2>
            <div className="w-12 h-px bg-lux-gold mx-auto mb-10" />
            <p className="font-body text-sm text-gray-500 leading-loose tracking-wide">
              AURÉNE is a study in minimalism. We strip away the unnecessary to focus on the
              textures, silhouettes, and craftsmanship that define modern elegance.
            </p>
          </div>
        </section>
      </div>
    </>
  );
}
